// @flow
import React, { useContext, useEffect } from "react";
import { Layout, Page } from "@shopify/polaris";
import { TitleBar } from "@shopify/app-bridge-react";
import { ViewMinor } from "@shopify/polaris-icons";

import { offerActions } from "store/offersStore";
import { shopActions } from "store/shopStore";
import { storeConnect } from "appStore";
import makeRequestProps from "store/makeRequestProps";

import { NavigationContext } from "contexts/Navigation";
import { ShopProvider } from "contexts/Shop";

import useDiscountsAppEmbedNeedsEnabling from "hooks/use-discounts-app-embed-needs-enabling";

import EditOfferFormWrapped from "./EditOfferFormWrapped";
import { OfferFormSkeletonPage } from "components/skeletons";
import MulticurrencyWarningInCard from "components/banners/MulticurrencyWarningInCard";
import MulticurrencyWarningBannerContainer from "components/banners/MulticurrencyWarningBannerContainer";
import { openPreviewTab } from "components/links";
import {
  leave as leaveSaveBarConfirmation,
  hide as hideSaveBar
} from "components/SaveBar";

import type { Offer, CreateOffer, PatchOffer } from "types/flow-types/offers";
import type { CurrentOfferRequest } from "appStore";
import type { ShopifyMultiCurrencyCode } from "types/flow-types/shopify";
import type { Flags } from "types/flow-types/flags";
import type { Shop, ShopEventPayload } from "types/flow-types/shop";

type Props = {
  appMultiCurrencyEnabled: boolean,
  createOffer: CreateOffer,
  currency: string,
  currentOfferRequest: CurrentOfferRequest,
  domain: string,
  enabledPresentmentCurrencies: ShopifyMultiCurrencyCode[],
  flags: Flags,
  match: {
    params: {
      offerId: number
    }
  },
  moneyFormat: string,
  offer: Offer,
  offerId: number,
  offersMethod: string,
  patchOffer: PatchOffer,
  setCurrentOffer: (?number) => void,
  setEvent: ShopEventPayload => void,
  shop: Shop
};

function OffersRoute(props: Props) {
  const navigation = useContext(NavigationContext);
  const {
    loading: discountsAppEmbedNeedsEnablingLoading,
    needsEnabling: discountsAppEmbedNeedsEnabling
  } = useDiscountsAppEmbedNeedsEnabling();

  useEffect(() => {
    const { setCurrentOffer, offerId } = props;
    setCurrentOffer(offerId);
  }, []);

  function handleSubmit(values, formikHelpers) {
    const { setCurrentOffer, createOffer, patchOffer, currentOfferRequest } =
      props;

    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

    /* If the current offer doesn't have an ID then this is a new offer
       and it needs to be created in the DB */
    if (currentOfferRequest.currentOffer.id) {
      return new Promise(resolve => {
        patchOffer(values.id, values, () => {
          setCurrentOffer(values.id);
          formikHelpers.resetForm({ values });
          hideSaveBar();
          resolve();
        });
      });
    } else {
      return new Promise(resolve => {
        createOffer(values, id => {
          navigation.replaceURL(`/offers/${id}`);
          setCurrentOffer(id);
          formikHelpers.resetForm({ values: { ...values, id } });
          hideSaveBar();
          resolve();
        });
      });
    }
  }

  async function onRedirectClick(redirectLocation: string) {
    await leaveSaveBarConfirmation();
    navigation.goTo(redirectLocation);
  }

  const {
    appMultiCurrencyEnabled,
    currency,
    currentOfferRequest,
    domain,
    enabledPresentmentCurrencies,
    flags,
    moneyFormat,
    offersMethod,
    setEvent,
    shop
  } = props;

  const { currentOffer: offer, loading } = currentOfferRequest;

  const isNewOffer = !offer?.id;
  const title = offer?.name || "";

  const multicurrencyWarningCard = (
    <MulticurrencyWarningInCard
      appMultiCurrencyEnabled={appMultiCurrencyEnabled}
      enabledPresentmentCurrencies={enabledPresentmentCurrencies}
      offersMethod={offersMethod}
    />
  );

  const previewAction = {
    id: "preview-action",
    content: "Preview",
    icon: ViewMinor,
    onAction: () => {
      openPreviewTab(offer, domain);
    }
  };

  const secondaryActions =
    loading ||
    discountsAppEmbedNeedsEnablingLoading ||
    isNewOffer ||
    offer.type === "PostPurchaseOffer" ||
    discountsAppEmbedNeedsEnabling
      ? []
      : [previewAction];

  const ppuSettingsMessageDismissed =
    shop.events.ppu_settings_message_dismissed;

  const dismissPPUSettingsMessage = () => {
    setEvent({ ppu_settings_message_dismissed: true });
  };

  const ppuInfoMessageDismissed = shop.events.ppu_info_message_dismissed;
  const dismissPPUInfoMessage = () => {
    setEvent({ ppu_info_message_dismissed: true });
  };
  const appPlan = shop.app_plan?.name;

  return (
    <>
      <TitleBar />
      <Page
        title={title}
        breadcrumbs={[
          {
            content: "Dashboard",
            onAction: () => onRedirectClick("/")
          }
        ]}
        secondaryActions={secondaryActions}
      >
        {loading ? (
          <OfferFormSkeletonPage />
        ) : (
          <>
            <Layout>
              <MulticurrencyWarningBannerContainer page="offer" />
              <Layout.Section>
                <ShopProvider value={shop}>
                  <EditOfferFormWrapped
                    key={offer?.id || "new"} // Force remount when offer changes
                    appPlan={appPlan}
                    offer={offer}
                    currency={currency}
                    dismissPPUInfoMessage={dismissPPUInfoMessage}
                    dismissPPUSettingsMessage={dismissPPUSettingsMessage}
                    domain={domain}
                    moneyFormat={moneyFormat}
                    isNewOffer={isNewOffer}
                    onSubmit={handleSubmit}
                    ppuInfoMessageDismissed={ppuInfoMessageDismissed}
                    ppuSettingsMessageDismissed={ppuSettingsMessageDismissed}
                    flags={flags}
                    multicurrencyWarningCard={multicurrencyWarningCard}
                    redirectToAppearance={event => {
                      event.preventDefault();
                      onRedirectClick("/appearance");
                    }}
                  />
                </ShopProvider>
              </Layout.Section>
            </Layout>
          </>
        )}
      </Page>
    </>
  );
}

const mapStateToProps = state => {
  const { app_settings, settings, flags } = state.shop.data;

  const { domain, money_format, currency, enabled_presentment_currencies } =
    settings;

  return {
    appMultiCurrencyEnabled: app_settings.enableMultiCurrency,
    currency,
    currentOfferRequest: makeRequestProps(state, "currentOffer"),
    domain,
    enabledPresentmentCurrencies: enabled_presentment_currencies,
    flags,
    moneyFormat: money_format,
    offersMethod: app_settings.offers_method,
    shop: state.shop.data
  };
};

export default storeConnect(
  OffersRoute,
  mapStateToProps,
  offerActions,
  shopActions
);
